import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, History, Science } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import type { GridColumn } from './EditableGrid';
import type { MutableRefObject } from 'react';
import { formatQuoteAmount } from '../../utils/quoteCurrency';
import { SkillLevelChip } from '../schedule/SkillLevelChip';

/**
 * Format a stored CAD value as currency for a grid cell. Honors the column's
 * `currency` + `fxRateToCad` (BUG-2026-0120 #6); defaults to CAD with no
 * conversion when those are absent, preserving every other call site.
 */
function formatCurrencyCell(value: unknown, col: GridColumn): string {
  return formatQuoteAmount(Number(value), col.currency ?? 'CAD', col.fxRateToCad ?? 1);
}

interface GridCellRendererProps {
  row: Record<string, unknown>;
  col: GridColumn;
  rowIndex: number;
  isActive: boolean;
  pendingSaveRef: MutableRefObject<number | null>;
  onRowChange: (rowIndex: number, field: string, value: unknown) => void;
  onAction?: (row: Record<string, unknown>) => void;
  onRowDelete?: (row: Record<string, unknown>) => void;
  onRowHistory?: (row: Record<string, unknown>) => void;
}

export const GridCellRenderer = ({
  row,
  col,
  rowIndex,
  isActive,
  pendingSaveRef,
  onRowChange,
  onAction,
  onRowDelete,
  onRowHistory,
}: GridCellRendererProps) => {
  const theme = useTheme();
  const value = row[col.field] ?? '';
  const isEmpty = value === '' || value == null;
  const showRequiredError = col.required && isEmpty;

  // Actions cells render their icons BEFORE any readonly logic — the beaker
  // (open workflow) and history are view affordances open to every role, and
  // marking a row/column readonly must never blank them (BUG: technician
  // impersonation lost the test icons). Destructive icons are gated by their
  // handlers simply not being passed (e.g. onRowDelete is admin-only).
  if (col.type === 'actions') {
    return renderActionsCell();
  }

  // Read-only fields always render as plain text. getCellReadonly (RBAC
  // Phase C) locks individual cells; a string return doubles as the tooltip
  // explaining why (e.g. "requires skill level 4 in Rock Direct Shear").
  const cellLock = col.getCellReadonly?.(row);
  if (col.readonly || cellLock) {
    const text = (
      <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', px: 0.5, color: alpha(theme.palette.text.primary, 0.7) }}>
        {isEmpty ? '' : col.formatCurrency ? formatCurrencyCell(value, col) : String(value)}
      </Typography>
    );
    return typeof cellLock === 'string'
      ? (
        <Tooltip title={cellLock} arrow>
          <Box sx={{ minHeight: 20, cursor: 'not-allowed' }}>{text}</Box>
        </Tooltip>
      )
      : text;
  }

  function renderActionsCell() {
    return (
      <Box sx={{ display: 'flex', gap: 0 }}>
        <Tooltip title="Open Test Workflow" arrow>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onAction?.(row); }}
            sx={{ color: theme.customColors.maroon, '&:hover': { backgroundColor: alpha(theme.customColors.maroon, 0.08) } }}
          >
            <Science sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {onRowDelete && (
          <Tooltip title="Remove from work order" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onRowDelete(row); }}
              sx={{ color: 'error.main', '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) } }}
            >
              <DeleteOutline sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {onRowHistory && (
          <Tooltip title="History — who changed what on this test" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onRowHistory(row); }}
              sx={{ color: 'text.secondary', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) } }}
            >
              <History sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  if (col.type === 'date') {
    return (
      <TextField
        size="small"
        fullWidth
        type="date"
        value={String(value).split('T')[0] || ''}
        onChange={(e) => onRowChange(rowIndex, col.field, e.target.value)}
        onFocus={() => { pendingSaveRef.current = null; }}
        InputLabelProps={{ shrink: true }}
        sx={{
          '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.8rem' },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.border.color, 0.4),
          },
        }}
      />
    );
  }

  // REQ-06 (round 2): key/label option pairs (e.g. GUID stored, name shown).
  // optionItems takes precedence over the plain string `options` list.
  // Phase C: getOptionsForRow lets signoff dropdowns vary per row (skill-scoped);
  // getRichOptionsForRow additionally carries each person's level in the row's
  // skill, rendered as a SkillLevelChip beside the name.
  // A DEFINED-but-empty rich list is honored as "no valid options" (renders
  // only '--') — it must NOT fall through to the static/plain lists, which
  // are role-based (e.g. Reviewed By with no qualified reviewers).
  const richOptions = col.getRichOptionsForRow?.(row);
  const rowOptions = col.getOptionsForRow?.(row) ?? col.options;
  if (col.type === 'select' && richOptions !== undefined) {
    return (
      <Select
        size="small"
        fullWidth
        variant="outlined"
        value={String(value)}
        error={showRequiredError}
        onChange={(e) => onRowChange(rowIndex, col.field, e.target.value)}
        onFocus={() => { pendingSaveRef.current = null; }}
        // Selected value renders as plain text (the chip belongs in the menu).
        renderValue={(v) => <span>{String(v)}</span>}
        MenuProps={{ PaperProps: { sx: { minWidth: 240 } } }}
        sx={{
          fontSize: '0.8rem',
          '& .MuiSelect-select': { py: 0.5, px: 1 },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: showRequiredError
              ? alpha(theme.palette.error.main, 0.5)
              : alpha(theme.border.color, 0.4),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: showRequiredError
              ? theme.palette.error.main
              : alpha(theme.border.color, 0.7),
          },
        }}
      >
        <MenuItem value="">--</MenuItem>
        {richOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <span style={{ flex: 1 }}>{opt.value}</span>
              <SkillLevelChip level={opt.level ?? null} pixelSize={18} />
            </Box>
          </MenuItem>
        ))}
      </Select>
    );
  }
  if (col.type === 'select' && (rowOptions || col.optionItems)) {
    return (
      <Select
        size="small"
        fullWidth
        variant="outlined"
        value={String(value)}
        error={showRequiredError}
        onChange={(e) => onRowChange(rowIndex, col.field, e.target.value)}
        onFocus={() => { pendingSaveRef.current = null; }}
        sx={{
          fontSize: '0.8rem',
          '& .MuiSelect-select': { py: 0.5, px: 1 },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: showRequiredError
              ? alpha(theme.palette.error.main, 0.5)
              : alpha(theme.border.color, 0.4),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: showRequiredError
              ? theme.palette.error.main
              : alpha(theme.border.color, 0.7),
          },
        }}
      >
        <MenuItem value="">--</MenuItem>
        {col.optionItems
          ? col.optionItems.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>
                {opt.label}
              </MenuItem>
            ))
          : rowOptions!.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>{opt}</MenuItem>
            ))}
        {col.allowCustomOption && (
          <>
            <Divider />
            <MenuItem
              value="__add_new__"
              sx={{ fontSize: '0.78rem', fontStyle: 'italic', color: 'primary.main' }}
              onClick={(e) => {
                e.stopPropagation();
                const input = window.prompt('Enter new option:');
                if (input?.trim()) {
                  col.onCustomOptionAdded?.(input.trim());
                  onRowChange(rowIndex, col.field, input.trim());
                }
              }}
            >
              + Add new…
            </MenuItem>
          </>
        )}
      </Select>
    );
  }

  if (!isActive) {
    const isSampleRef = col.field === 'SampleId';
    return (
      <Typography
        variant="body2"
        noWrap
        onClick={isSampleRef && onAction ? (e) => { e.stopPropagation(); onAction(row); } : undefined}
        sx={{
          fontSize: '0.8rem',
          px: 0.5,
          ...(showRequiredError && {
            color: theme.palette.error.main,
            fontStyle: 'italic',
          }),
          ...(isSampleRef && !isEmpty && {
            color: theme.palette.primary.main,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline', color: theme.palette.primary.dark },
          }),
        }}
      >
        {isEmpty
          ? (showRequiredError ? 'Required' : '')
          : col.formatCurrency
            ? formatCurrencyCell(value, col)
            : String(value)}
      </Typography>
    );
  }

  return (
    <TextField
      size="small"
      fullWidth
      autoComplete="off"
      error={showRequiredError}
      type={col.type === 'number' ? 'number' : 'text'}
      value={String(value)}
      onChange={(e) => onRowChange(rowIndex, col.field, e.target.value)}
      onFocus={() => { pendingSaveRef.current = null; }}
      sx={{
        '& .MuiInputBase-input': {
          py: 0.5,
          px: 1,
          fontSize: '0.8rem',
          ...(!col.showSpinButtons && {
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            MozAppearance: 'textfield',
          }),
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(theme.palette.primary.main, 0.5),
        },
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
      }}
    />
  );
};
